/**
 * Service Financial Report
 * Comprehensive financial analysis for service-based businesses
 * Task: Phase 4 - Pilates/Yoga Financial Monitoring
 * Applicable to: Pilates/Yoga, Health Clinics, Beauty Salons, Veterinary
 */

import { useState, useMemo } from 'react';
import { IndustryService, IndustryAppointment } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, FileText, DollarSign, TrendingUp, Users, Calendar } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, parseISO, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ServiceFinancialReportProps {
  services: IndustryService[];
  appointments: IndustryAppointment[];
  onExportPDF?: () => void;
  onExportCSV?: () => void;
}

type PeriodFilter = 'last_3_months' | 'last_6_months' | 'current_year' | 'all';

interface MonthlyMetrics {
  month: Date;
  revenue: number;
  appointmentCount: number;
  avgRevenuePerAppointment: number;
}

interface ServiceMetrics {
  service: IndustryService;
  appointmentCount: number;
  totalRevenue: number;
  avgRevenue: number;
  revenueShare: number;
}

export function ServiceFinancialReport({
  services,
  appointments,
  onExportPDF,
  onExportCSV,
}: ServiceFinancialReportProps) {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('last_6_months');

  // Calculate date range for period filter
  const dateRange = useMemo(() => {
    const now = new Date();
    let start: Date;

    switch (periodFilter) {
      case 'last_3_months':
        start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case 'last_6_months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'current_year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
      default:
        // Find earliest appointment date
        const earliestDate = appointments.reduce((earliest, apt) => {
          if (!apt.scheduled_at) return earliest;
          const aptDate = parseISO(apt.scheduled_at);
          return !earliest || aptDate < earliest ? aptDate : earliest;
        }, null as Date | null);
        start = earliestDate || new Date(now.getFullYear(), 0, 1);
    }

    return { start, end: now };
  }, [periodFilter, appointments]);

  // Filter appointments by period
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      if (!apt.scheduled_at) return false;
      const aptDate = parseISO(apt.scheduled_at);
      return isWithinInterval(aptDate, { start: dateRange.start, end: dateRange.end });
    });
  }, [appointments, dateRange]);

  // Build service lookup map
  const serviceMap = useMemo(() => {
    return services.reduce((acc, service) => {
      acc[service.id] = service;
      return acc;
    }, {} as Record<number, IndustryService>);
  }, [services]);

  // Calculate monthly metrics
  const monthlyMetrics = useMemo(() => {
    const months = eachMonthOfInterval({ start: dateRange.start, end: dateRange.end });

    return months.map((month): MonthlyMetrics => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const monthAppointments = filteredAppointments.filter(apt => {
        if (!apt.scheduled_at) return false;
        const aptDate = parseISO(apt.scheduled_at);
        return isWithinInterval(aptDate, { start: monthStart, end: monthEnd });
      });

      const revenue = monthAppointments.reduce((sum, apt) => {
        const service = serviceMap[apt.service_id];
        return sum + (service?.price || 0);
      }, 0);

      const appointmentCount = monthAppointments.length;
      const avgRevenuePerAppointment = appointmentCount > 0 ? revenue / appointmentCount : 0;

      return {
        month,
        revenue,
        appointmentCount,
        avgRevenuePerAppointment,
      };
    });
  }, [dateRange, filteredAppointments, serviceMap]);

  // Calculate service metrics
  const serviceMetrics = useMemo(() => {
    const totalRevenue = filteredAppointments.reduce((sum, apt) => {
      const service = serviceMap[apt.service_id];
      return sum + (service?.price || 0);
    }, 0);

    const metrics: ServiceMetrics[] = services.map(service => {
      const serviceAppointments = filteredAppointments.filter(apt => apt.service_id === service.id);
      const appointmentCount = serviceAppointments.length;
      const revenue = appointmentCount * (service.price || 0);
      const avgRevenue = appointmentCount > 0 ? revenue / appointmentCount : 0;
      const revenueShare = totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;

      return {
        service,
        appointmentCount,
        totalRevenue: revenue,
        avgRevenue,
        revenueShare,
      };
    });

    // Sort by revenue descending
    return metrics.sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [services, filteredAppointments, serviceMap]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const totalRevenue = monthlyMetrics.reduce((sum, m) => sum + m.revenue, 0);
    const totalAppointments = monthlyMetrics.reduce((sum, m) => sum + m.appointmentCount, 0);
    const avgRevenuePerAppointment = totalAppointments > 0 ? totalRevenue / totalAppointments : 0;
    const activeServices = services.filter(s => s.is_active !== false).length;

    // Calculate monthly average revenue
    const avgMonthlyRevenue = monthlyMetrics.length > 0 ? totalRevenue / monthlyMetrics.length : 0;

    // Calculate growth trend (last month vs previous month)
    let growthTrend = 0;
    if (monthlyMetrics.length >= 2) {
      const lastMonth = monthlyMetrics[monthlyMetrics.length - 1];
      const previousMonth = monthlyMetrics[monthlyMetrics.length - 2];
      if (previousMonth.revenue > 0) {
        growthTrend = ((lastMonth.revenue - previousMonth.revenue) / previousMonth.revenue) * 100;
      }
    }

    return {
      totalRevenue,
      totalAppointments,
      avgRevenuePerAppointment,
      avgMonthlyRevenue,
      activeServices,
      growthTrend,
    };
  }, [monthlyMetrics, services]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    const formatted = value.toFixed(1);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Relatório Financeiro</h2>
          <p className="text-muted-foreground">Análise detalhada de receita e performance</p>
        </div>
        <div className="flex gap-2">
          {onExportCSV && (
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <FileText className="mr-2 h-4 w-4" />
              Exportar CSV
            </Button>
          )}
          {onExportPDF && (
            <Button variant="outline" size="sm" onClick={onExportPDF}>
              <Download className="mr-2 h-4 w-4" />
              Exportar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Period Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select value={periodFilter} onValueChange={(val) => setPeriodFilter(val as PeriodFilter)}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last_3_months">Últimos 3 meses</SelectItem>
            <SelectItem value="last_6_months">Últimos 6 meses</SelectItem>
            <SelectItem value="current_year">Ano atual</SelectItem>
            <SelectItem value="all">Todo o período</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Receita Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Média mensal: {formatCurrency(stats.avgMonthlyRevenue)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Total de Atendimentos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalAppointments}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Ticket médio: {formatCurrency(stats.avgRevenuePerAppointment)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Crescimento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.growthTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercent(stats.growthTrend)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Último mês vs anterior
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Serviços Ativos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeServices}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Total de {services.length} serviços
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução Mensal de Receita</CardTitle>
          <CardDescription>Receita e atendimentos por mês</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Mês</TableHead>
                  <TableHead className="text-right">Atendimentos</TableHead>
                  <TableHead className="text-right">Receita</TableHead>
                  <TableHead className="text-right">Ticket Médio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyMetrics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      Nenhum dado encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  monthlyMetrics.map((metrics, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {format(metrics.month, 'MMMM yyyy', { locale: ptBR })}
                      </TableCell>
                      <TableCell className="text-right">{metrics.appointmentCount}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(metrics.revenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(metrics.avgRevenuePerAppointment)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Service Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Performance por Serviço</CardTitle>
          <CardDescription>Receita e participação de cada serviço</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Serviço</TableHead>
                  <TableHead className="text-right">Atendimentos</TableHead>
                  <TableHead className="text-right">Receita Total</TableHead>
                  <TableHead className="text-right">Valor Unitário</TableHead>
                  <TableHead className="text-right">% Participação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serviceMetrics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      Nenhum serviço encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  serviceMetrics.map((metrics) => (
                    <TableRow key={metrics.service.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metrics.service.name}</div>
                          {metrics.service.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {metrics.service.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{metrics.appointmentCount}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(metrics.totalRevenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(metrics.service.price || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium">{metrics.revenueShare.toFixed(1)}%</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
